import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, TrendUp, Pencil, Trash } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  id: '',
  employee_id: '',
  hike_date: new Date().toISOString().split('T')[0],
  old_salary: { basic: 0, hra: 0, con: 0, medical: 0, non_taxable: 0, special_allowance: 0 },
  new_salary: { basic: 0, hra: 0, con: 0, medical: 0, non_taxable: 0, special_allowance: 0 },
};

export const SalaryHikesPage = () => {
  const [hikes, setHikes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [search, setSearch] = useState('');
  // Hike calculator
  const [hikeMode, setHikeMode] = useState('percent'); // percent | amount
  const [hikeValue, setHikeValue] = useState(0);

  const filteredHikes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hikes.filter((h) => {
      if (!q) return true;
      const emp = employees.find((e) => e.id === h.employee_id);
      return `${emp?.name || ''} ${emp?.emp_id || ''}`.toLowerCase().includes(q);
    });
  }, [hikes, employees, search]);
  const { paged: pagedHikes, controls: hikeControls, startIdx: hikeStart } = usePagination(filteredHikes, { storageKey: 'hikes-page-size', testIdPrefix: 'hike' });

  useEffect(() => {
    loadData();
  }, []);

  const recalculate = () => {
    const old = formData.old_salary || {};
    const oldTotal = Object.values(old).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    if (oldTotal === 0) {
      alert('Please select an employee first (old salary must be > 0).');
      return;
    }
    const value = parseFloat(hikeValue) || 0;
    if (value <= 0) {
      alert('Enter a positive % or ₹ value.');
      return;
    }
    const next = {};
    if (hikeMode === 'percent') {
      Object.keys(old).forEach((k) => {
        next[k] = Math.round((parseFloat(old[k]) || 0) * (1 + value / 100));
      });
    } else {
      // Distribute ₹ amount proportionally to old breakdown
      Object.keys(old).forEach((k) => {
        const share = (parseFloat(old[k]) || 0) / oldTotal;
        next[k] = Math.round((parseFloat(old[k]) || 0) + value * share);
      });
    }
    setFormData({ ...formData, new_salary: next });
  };

  const loadData = async () => {
    try {
      const [hikesRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/salary-hikes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
      ]);
      setHikes(hikesRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find((e) => e.id === empId);
    if (emp) {
      setFormData({
        ...formData,
        employee_id: empId,
        old_salary: emp.salary_components,
        new_salary: emp.salary_components,
      });
    }
  };

  const handleEdit = (hike) => {
    setFormData({
      id: hike.id,
      employee_id: hike.employee_id,
      hike_date: hike.hike_date,
      old_salary: { ...hike.old_salary },
      new_salary: { ...hike.new_salary },
    });
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    return handleDeleteHikeId(id);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        if (!formData.id) {
          toast.error('Cannot edit — this hike record is missing its ID. Click Refresh and try again (the server will auto-heal it).');
          return;
        }
        if (!formData.employee_id) { toast.error('Employee is missing — close and re-open the edit dialog'); return; }
        await axios.put(`${API_URL}/api/salary-hikes/${formData.id}`, formData, { withCredentials: true });
        toast.success('Salary hike updated');
      } else {
        await axios.post(`${API_URL}/api/salary-hikes`, formData, { withCredentials: true });
        toast.success('Salary hike recorded');
      }
      setOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      const status = error.response?.status;
      let detail = error.response?.data?.detail || error.message;
      if (typeof detail !== 'string') detail = JSON.stringify(detail);
      // Specific hint for 405 — almost always means production backend hasn't picked up the new PUT endpoint
      if (status === 405) {
        detail = 'Backend rejected PUT (Method Not Allowed). On production, run `bash deploy/deploy-full.sh` to restart the backend so the latest API routes are picked up.';
      }
      toast.error('Save failed: ' + detail);
    }
  };

  const handleDeleteHikeId = async (id) => {
    if (!id) {
      toast.error('Cannot delete — this hike record is missing its ID. Click Refresh and try again.');
      return;
    }
    if (!window.confirm('Delete this salary hike? Employee salary will revert to the old amount.')) return;
    try {
      await axios.delete(`${API_URL}/api/salary-hikes/${id}`, { withCredentials: true });
      toast.success('Salary hike deleted');
      loadData();
    } catch (error) {
      const status = error.response?.status;
      let detail = error.response?.data?.detail || error.message;
      if (typeof detail !== 'string') detail = JSON.stringify(detail);
      if (status === 405) {
        detail = 'Backend rejected DELETE (Method Not Allowed). On production, run `bash deploy/deploy-full.sh` to restart the backend.';
      }
      toast.error('Delete failed: ' + detail);
    }
  };

  const getTotalSalary = (components) => {
    return Object.values(components).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const getHikePercentage = (oldComp, newComp) => {
    const oldTotal = getTotalSalary(oldComp);
    const newTotal = getTotalSalary(newComp);
    if (oldTotal === 0) return 0;
    return (((newTotal - oldTotal) / oldTotal) * 100).toFixed(2);
  };

  return (
    <div data-testid="salary-hikes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Salary Hikes
          </h1>
          <p className="text-[#52525B] text-sm">Track salary increments and revisions</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-hike-btn"
              className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
            >
              <Plus size={20} className="mr-2" />
              Add Salary Hike
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Salary Hike' : 'Add Salary Hike'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Employee</Label>
                  <Select value={formData.employee_id} onValueChange={handleEmployeeSelect} disabled={editMode}>
                    <SelectTrigger data-testid="hike-employee-select">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.filter((emp) => !!emp.id).map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.emp_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hike Date</Label>
                  <Input
                    type="date"
                    value={formData.hike_date}
                    onChange={(e) => setFormData({ ...formData, hike_date: e.target.value })}
                    required
                    data-testid="hike-date-input"
                  />
                </div>
              </div>

              {/* Hike calculator */}
              <div className="border rounded-md p-3 bg-emerald-50/40 space-y-2">
                <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1">
                  <TrendUp size={14} weight="bold" /> Auto-calculate New Salary
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="radio" name="hikeMode" checked={hikeMode === 'percent'} onChange={() => setHikeMode('percent')} data-testid="hike-mode-percent" />
                    Hike by %
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="radio" name="hikeMode" checked={hikeMode === 'amount'} onChange={() => setHikeMode('amount')} data-testid="hike-mode-amount" />
                    Hike by ₹
                  </label>
                  <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                    <span className="text-xs text-gray-600">{hikeMode === 'percent' ? 'Percent:' : 'Amount:'}</span>
                    <Input
                      type="number"
                      step={hikeMode === 'percent' ? '0.1' : '100'}
                      value={hikeValue}
                      onChange={(e) => setHikeValue(e.target.value)}
                      placeholder={hikeMode === 'percent' ? 'e.g. 10' : 'e.g. 5000'}
                      className="h-8 max-w-[140px]"
                      data-testid="hike-value-input"
                    />
                    {hikeMode === 'percent' ? <span className="text-xs">%</span> : <span className="text-xs">₹</span>}
                    <Button type="button" size="sm" variant="outline" onClick={recalculate} data-testid="hike-recalc-btn" className="ml-2">
                      Recalculate
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500">Distributes the increase proportionally across all 6 components based on the current breakdown. You can still tweak individual fields after.</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="border rounded-sm p-4">
                  <h3 className="font-semibold mb-4 text-sm">Old Salary Components</h3>
                  <div className="space-y-3">
                    {Object.keys(formData.old_salary).map((key) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs capitalize">{key.replace('_', ' ')}</Label>
                        <Input
                          type="number"
                          value={formData.old_salary[key]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              old_salary: { ...formData.old_salary, [key]: parseFloat(e.target.value) || 0 },
                            })
                          }
                          data-testid={`old-salary-${key}-input`}
                        />
                      </div>
                    ))}
                    <div className="pt-3 border-t font-semibold flex justify-between">
                      <span>Total:</span>
                      <span>₹{getTotalSalary(formData.old_salary).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="border rounded-sm p-4">
                  <h3 className="font-semibold mb-4 text-sm">New Salary Components</h3>
                  <div className="space-y-3">
                    {Object.keys(formData.new_salary).map((key) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs capitalize">{key.replace('_', ' ')}</Label>
                        <Input
                          type="number"
                          value={formData.new_salary[key]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              new_salary: { ...formData.new_salary, [key]: parseFloat(e.target.value) || 0 },
                            })
                          }
                          data-testid={`new-salary-${key}-input`}
                        />
                      </div>
                    ))}
                    <div className="pt-3 border-t font-semibold flex justify-between">
                      <span>Total:</span>
                      <span className="text-[#16A34A]">₹{getTotalSalary(formData.new_salary).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#F4F4F5] rounded-sm flex items-center justify-between">
                <span className="text-sm font-semibold">Hike Percentage:</span>
                <span className="text-2xl font-bold text-[#16A34A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {getHikePercentage(formData.old_salary, formData.new_salary)}%
                </span>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-hike-btn">
                  Save Hike
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E4E4E7]">
        <div className="p-3 border-b">
          <Input
            placeholder="Search by employee name, emp ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="hike-search"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F5]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Employee</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Old Salary</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">New Salary</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Hike %</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedHikes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-[#52525B]">
                  {hikes.length === 0 ? 'No salary hikes recorded yet.' : 'No matches.'}
                </TableCell>
              </TableRow>
            ) : pagedHikes.map((hike, idx) => {
              const emp = employees.find((e) => e.id === hike.employee_id);
              return (
                <TableRow key={hike.id} data-testid={`hike-row-${hike.id}`}>
                  <TableCell className="text-gray-400">{hikeStart + idx + 1}</TableCell>
                  <TableCell className="font-medium">
                    {emp ? (
                      <div>
                        <div>{emp.name}</div>
                        <div className="text-xs text-[#52525B]">{emp.emp_id}</div>
                      </div>
                    ) : (
                      'Unknown'
                    )}
                  </TableCell>
                  <TableCell>{(() => { const [y, m, d] = String(hike.hike_date || '').slice(0, 10).split('-'); return y ? `${d}/${m}/${y}` : hike.hike_date; })()}</TableCell>
                  <TableCell>₹{getTotalSalary(hike.old_salary).toLocaleString()}</TableCell>
                  <TableCell className="font-semibold text-[#16A34A]">
                    ₹{getTotalSalary(hike.new_salary).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TrendUp size={16} className="text-[#16A34A]" weight="bold" />
                      <span className="font-semibold text-[#16A34A]">
                        {getHikePercentage(hike.old_salary, hike.new_salary)}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(hike)}
                        data-testid={`edit-hike-${hike.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[#DC2626] hover:text-[#DC2626]"
                        onClick={() => handleDelete(hike.id)}
                        data-testid={`delete-hike-${hike.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {hikeControls}
      </Card>
    </div>
  );
};
